import { DeleteOutline } from '@mui/icons-material';
import {
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FC, useCallback, useState } from 'react';
import Dropzone from 'react-dropzone';
import API from 'api/API';
import { UploadTypeMapping } from 'api/Responses/BulkUploadResponses';
import { useLanguage } from 'context/AppContext';
import useDataUpload from 'hooks/useDataUpload';
import { acceptedFileType, getFileUploadInfo } from '../DataUpload.helpers';
import * as BaseStyles from '../DataUpload.styles';
import DataUploadPageTemplate from '../molecules/DataUploadPageTemplate';
import * as Styles from './DataUpload.styles';

interface DataUploadParams {}

const DataUploadPage: FC = () => {
  const [language] = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const { updateDataUploadFileType, uploadedFiles, addFiles, deleteFile, reset, submitFiles, countOfUploadedFiles } =
    useDataUpload();

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      try {
        setIsLoading(true);
        await addFiles(acceptedFiles);
      } catch (error) {
        API.errorHandler(error);
      } finally {
        setIsLoading(false);
      }
    },
    [addFiles],
  );

  const handleSubmitFiles = useCallback(async () => {
    setIsLoading(true);
    try {
      await submitFiles();
    } catch (error) {
      API.errorHandler(error);
    } finally {
      reset();
    }
    setIsLoading(false);
  }, [reset, submitFiles]);

  const handleChangeDataUploadFileType = useCallback(
    (e: SelectChangeEvent<UploadTypeMapping>, id: string) => {
      updateDataUploadFileType(id, e.target.value as UploadTypeMapping);
    },
    [updateDataUploadFileType],
  );

  return (
    <DataUploadPageTemplate>
      <Styles.DataUploadPage>
        <Styles.DropzoneContainer>
          <Dropzone onDrop={handleDrop} accept={acceptedFileType}>
            {({ getRootProps, getInputProps }) => (
              <Styles.FileUploadContainer>
                <Styles.FileUploadRoot {...getRootProps()}>
                  <input {...getInputProps()} />
                  <Styles.FileUploadContent>
                    <Styles.FileUploadIcon />
                    <Styles.FileUploadInfo>
                      <Typography variant="subtitle1" gutterBottom>
                        {language.DataUpload.fileUpload.text} {language.DataUpload.fileUpload.browse}
                      </Typography>
                      <Typography variant="subtitle2">
                        {countOfUploadedFiles + ' ' + language.DataUpload.fileUpload.countUploadedFiles}
                      </Typography>
                    </Styles.FileUploadInfo>
                  </Styles.FileUploadContent>
                </Styles.FileUploadRoot>
              </Styles.FileUploadContainer>
            )}
          </Dropzone>
        </Styles.DropzoneContainer>
        <Styles.FileUploadList>
          {countOfUploadedFiles > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{language.DataUpload.fileUpload.type}</TableCell>
                  <TableCell>{language.DataUpload.fileUpload.name}</TableCell>
                  <TableCell>{language.DataUpload.fileUpload.delete}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(uploadedFiles).map(([id, uploadedFile]) => {
                  return (
                    <Styles.FileUploadItem key={id}>
                      <TableCell>
                        <FormControl size="small" variant="outlined" style={{ width: 120 }}>
                          <Select
                            defaultValue={UploadTypeMapping.DEVICE_DATA}
                            onChange={e => handleChangeDataUploadFileType(e, id)}
                          >
                            <MenuItem value={UploadTypeMapping.DEVICE_DATA}>{UploadTypeMapping.DEVICE_DATA}</MenuItem>
                            <MenuItem value={UploadTypeMapping.BOM_DATA}>{UploadTypeMapping.BOM_DATA}</MenuItem>
                            <MenuItem value={UploadTypeMapping.DEVICE_ATTACHMENT}>
                              {UploadTypeMapping.DEVICE_ATTACHMENT}
                            </MenuItem>
                            <MenuItem value={UploadTypeMapping.BOM_ATTACHMENT}>
                              {UploadTypeMapping.BOM_ATTACHMENT}
                            </MenuItem>
                            <MenuItem value={UploadTypeMapping.USS_DATA}>{UploadTypeMapping.USS_DATA}</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <Styles.Cell size="medium">
                        <Styles.TableCellDiv>{getFileUploadInfo(uploadedFile.file)}</Styles.TableCellDiv>
                      </Styles.Cell>
                      <TableCell>
                        <IconButton onClick={() => deleteFile(id)}>
                          <DeleteOutline />
                        </IconButton>
                      </TableCell>
                    </Styles.FileUploadItem>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="subtitle2">{language.DataUpload.fileUpload.noUpload}</Typography>
          )}
        </Styles.FileUploadList>
        <Styles.FileUploadActionsContainer>
          <BaseStyles.DataButton disabled={countOfUploadedFiles === 0} onClick={reset}>
            {language.DataUpload.fileUpload.cancel}
          </BaseStyles.DataButton>
          <BaseStyles.DataButton disabled={countOfUploadedFiles === 0 || isLoading} onClick={handleSubmitFiles}>
            {isLoading ? <CircularProgress /> : language.DataUpload.fileUpload.submit}
          </BaseStyles.DataButton>
        </Styles.FileUploadActionsContainer>
      </Styles.DataUploadPage>
    </DataUploadPageTemplate>
  );
};

export default DataUploadPage;
